/**
 * Pro entitlement state. Development builds can use local mock purchases. Production
 * builds require the native TruebendPurchases bridge to be injected by iOS.
 *
 * Truebend Pro is a single non-consumable "unlock" purchase — buy once, own it forever.
 * There is no plan, no renewal, and no expiration anywhere in this module.
 */

import { useEffect, useState } from 'react';
import * as storage from './storage.js';

const KEY = 'subscription';

export type Tier = 'free' | 'pro';
/**
 * Terminal states of a purchase/restore attempt.
 *
 * - `cancelled` is a deliberate user choice and MUST render as nothing at all.
 * - `pending` is Ask to Buy / SCA approval — informational, never an error.
 * - `unavailable` is the only retryable failure, and its copy is owned by the UI.
 *
 * Nothing here is ever derived from a native exception message.
 */
export type SubscriptionActionStatus =
  | 'purchased'
  | 'restored'
  | 'not-found'
  | 'cancelled'
  | 'pending'
  | 'unavailable';

/** Terminal outcome reported by the native bridge alongside the entitlement flag. */
export type NativePurchaseOutcome =
  | 'purchased'
  | 'restored'
  | 'cancelled'
  | 'pending'
  | 'not-found'
  | 'unavailable';

export const PRO_UNLOCK = {
  productId: 'com.fiveohninelectric.bender.pro.unlock',
  price: '$4.99',
  label: 'Unlock Pro — one-time',
} as const;

export interface PurchaseRuntimeConfig {
  mode?: string;
  enableMockPurchases?: boolean;
}

export interface NativePurchaseReceipt {
  active: boolean;
  /**
   * Optional: older native builds (and the Android Play Billing plugin) resolve with only
   * `active`. Absent means "infer from `active`" — see `actionResultFromNativeReceipt`.
   */
  outcome?: NativePurchaseOutcome;
}

/** Raw shape resolved by the native bridge's `loadProduct()`, before normalization. */
export interface NativeProductInfo {
  available: boolean;
  displayPrice?: string | undefined;
  productId?: string | undefined;
}

export interface NativePurchaseBridge {
  purchaseUnlock: () => Promise<NativePurchaseReceipt>;
  restorePurchases: () => Promise<NativePurchaseReceipt>;
  /**
   * Optional: older native builds (and any Android bridge) may not implement this yet.
   * `normalizeNativeBridge` carries it through only when present — its absence must
   * degrade to "no confirmed price," never throw. See `loadProUnlockProduct`.
   */
  loadProduct?: (() => Promise<NativeProductInfo>) | undefined;
  /**
   * Optional: older native builds may not implement this yet. Its absence means launch-time
   * entitlement reconciliation simply does not run — Restore Purchases remains the
   * fallback, exactly as it always was before this existed. See
   * `syncEntitlementFromNativeStoreKit`.
   */
  checkEntitlement?: (() => Promise<NativePurchaseReceipt>) | undefined;
}

/** The ONLY shape PaywallSheet may use to decide what price it can show. */
export interface ProProductInfo {
  available: boolean;
  displayPrice?: string | undefined;
  productId?: string | undefined;
}

interface State {
  tier: Tier;
  source: 'apple' | 'web' | 'mock';
  updatedAt?: number; // unix ms
}

interface SubscriptionActionResult {
  status: SubscriptionActionStatus;
  subscription: State;
}

declare global {
  interface Window {
    TruebendPurchases?: NativePurchaseBridge;
    Capacitor?: {
      isNativePlatform?: () => boolean;
      getPlatform?: () => string;
      Plugins?: {
        TruebendPurchases?: NativePurchaseBridge;
      };
    };
    /** Native push channel for a deferred/Ask-to-Buy purchase that completes later. */
    __truebendOnPurchaseUpdate?: (receipt: NativePurchaseReceipt) => void;
    /** Buffered update delivered by native before the listener above was installed. */
    __truebendPendingPurchaseUpdate?: NativePurchaseReceipt;
  }
}

const DEFAULT_STATE: State = { tier: 'free', source: 'mock' };

const listeners = new Set<() => void>();

function load(): State {
  return storage.get<State>(KEY) ?? DEFAULT_STATE;
}

function save(next: State): void {
  storage.set(KEY, next);
  listeners.forEach((fn) => fn());
}

export function getSubscription(): State {
  return load();
}

/**
 * The ONLY source of user-facing purchase copy. Both the paywall and Settings render from
 * here so the two can never drift — a duplicated copy of this switch in SettingsScreen is
 * how "Purchases are not configured for this build yet." survived a fix that removed it
 * from the paywall, and stayed reachable in a shipped App Store build.
 *
 * Two invariants:
 * 1. No string here is ever derived from a native error or exception message.
 * 2. `cancelled` returns '' — cancelling the App Store sheet is a normal choice and must
 *    leave no message behind, least of all one that reads as a failure.
 */
export function statusMessageFor(status: SubscriptionActionStatus): string {
  switch (status) {
    case 'purchased':
      return 'Pro unlocked.';
    case 'restored':
      return 'Purchases restored.';
    case 'pending':
      return 'Waiting for approval. Pro unlocks automatically once the purchase is approved.';
    case 'not-found':
      return 'No previous purchase found on this Apple ID.';
    case 'unavailable':
      return 'Pricing unavailable — check your connection.';
    case 'cancelled':
      return '';
    default:
      return '';
  }
}

export function canUseMockPurchases(config: PurchaseRuntimeConfig = readRuntimeConfig()): boolean {
  return config.enableMockPurchases === true || config.mode !== 'production';
}

export function canStartPurchaseFlow(config: PurchaseRuntimeConfig = readRuntimeConfig()): boolean {
  // On native iOS the purchase bridge user-script runs at document-start, but
  // React may render before window.TruebendPurchases is fully defined. Trust
  // the native context: the bridge will be ready by the time the user taps.
  if (isNativeIosContext()) return true;
  return getNativePurchaseBridge() !== null || canUseMockPurchases(config);
}

export function hasActivePro(config: PurchaseRuntimeConfig = readRuntimeConfig()): boolean {
  return isActiveSubscription(load(), config);
}

export const isPro = hasActivePro;

/**
 * Wire the native bridge's async purchase-update channel. The iOS bridge calls this
 * when a deferred/Ask-to-Buy transaction completes after the original purchase already
 * returned pending, so Pro activates without a manual restore. No-op on web.
 */
export function installNativePurchaseUpdateListener(): void {
  if (typeof window === 'undefined') return;
  window.__truebendOnPurchaseUpdate = (receipt: NativePurchaseReceipt) => {
    actionResultFromNativeReceipt(receipt, 'purchased');
  };

  // The native bridge buffers an update that landed before this listener existed — an
  // Ask-to-Buy approval clearing during app startup would otherwise be dropped.
  const buffered = window.__truebendPendingPurchaseUpdate;
  if (buffered?.active) {
    delete window.__truebendPendingPurchaseUpdate;
    actionResultFromNativeReceipt(buffered, 'purchased');
  }

  // Reconcile with StoreKit ownership directly, rather than waiting to be pushed to — see
  // syncEntitlementFromNativeStoreKit's doc comment for why the push-based approach could
  // silently drop a real entitlement on relaunch.
  void syncEntitlementFromNativeStoreKit();
}

/**
 * Reconcile Pro entitlement with StoreKit on launch — the reliable half of launch-time
 * entitlement sync, replacing a native PUSH that used to fire from
 * `TruebendPurchasesBridge.attach(to:)` before the web view had necessarily loaded far
 * enough to receive it (that code ran before `super.viewDidLoad()` even started the first
 * navigation). The push evaluated `window.__truebendPurchasesUpdate` against whatever page
 * happened to be loaded at that instant; if it fired before the injected script had run,
 * the check silently failed and the update was dropped — no retry, no buffer, because the
 * JS-side buffering only helps once that function itself already exists. A customer who
 * purchased, then relaunched with local storage cleared (delete/reinstall), stayed on Free
 * until they manually tapped Restore Purchases, despite already owning the unlock.
 *
 * This pulls instead: by the time this runs — from `installNativePurchaseUpdateListener()`,
 * called once at module load in main.tsx — `window.TruebendPurchases` can only already
 * exist, since the injected user script that defines it runs at document-start, before any
 * of the page's own scripts including this one. There is no race window to lose.
 *
 * Silent on failure and on an older/missing bridge: this is a background reconciliation,
 * never a user-initiated action, so it must never surface UI copy. Restore Purchases
 * remains available regardless, exactly as before this existed.
 */
export async function syncEntitlementFromNativeStoreKit(): Promise<void> {
  const nativeBridge = await waitForNativeBridge();
  if (!nativeBridge?.checkEntitlement) return;
  try {
    const receipt = await nativeBridge.checkEntitlement();
    if (receipt?.active) {
      actionResultFromNativeReceipt(receipt, 'restored');
    }
  } catch {
    // Silent — see doc comment above.
  }
}

/** Dev/test only: production purchases must come from the native bridge. */
export function setSubscriptionTier(tier: Tier): void {
  save({ tier, source: 'mock' });
}

export async function purchaseProUnlock(
  config: PurchaseRuntimeConfig = readRuntimeConfig(),
): Promise<SubscriptionActionResult> {
  // Wait briefly for the bridge on native iOS — user-script may still be
  // initialising when React renders the paywall.
  const nativeBridge = await waitForNativeBridge();
  if (nativeBridge) {
    return purchaseUnlockWithNativeBridge(nativeBridge);
  }

  if (!canUseMockPurchases(config)) {
    return { status: 'unavailable', subscription: load() };
  }

  const subscription: State = {
    tier: 'pro',
    source: 'mock',
    updatedAt: Date.now(),
  };
  save(subscription);
  return { status: 'purchased', subscription };
}

/**
 * The single source of truth for what price the paywall may display. It never resolves
 * `available: true` unless the price is confirmed sellable:
 *
 * - Native bridge with `loadProduct`: returns whatever StoreKit (or the equivalent native
 *   store) actually confirmed via `Product.products(for:)`.
 * - Mock/dev builds with no native bridge at all: the mock "purchase" always succeeds
 *   locally with no App Store round trip, so there is no unconfirmed price to guard
 *   against — this is never what App Review sees.
 * - Everything else (bridge present but missing `loadProduct`, or no bridge and mock
 *   purchases disabled): `available: false`. This is the fourth-rejection fix — a
 *   hardcoded price rendered next to a Buy button the App Store had not confirmed it could
 *   honor is exactly what drew the 2.1(b) rejections. PaywallSheet must render
 *   `displayPrice` from this result and must never read `PRO_UNLOCK.price` directly.
 */
export async function loadProUnlockProduct(
  config: PurchaseRuntimeConfig = readRuntimeConfig(),
): Promise<ProProductInfo> {
  const nativeBridge = await waitForNativeBridge();

  if (nativeBridge) {
    if (!nativeBridge.loadProduct) {
      // Older/Android bridge without this method yet — degrade, do not crash.
      return { available: false };
    }
    try {
      const info = await nativeBridge.loadProduct();
      if (info?.available && info.displayPrice) {
        return { available: true, displayPrice: info.displayPrice, productId: info.productId };
      }
      return { available: false };
    } catch {
      return { available: false };
    }
  }

  if (canUseMockPurchases(config)) {
    return { available: true, displayPrice: PRO_UNLOCK.price, productId: PRO_UNLOCK.productId };
  }

  return { available: false };
}

export async function restorePurchases(
  config: PurchaseRuntimeConfig = readRuntimeConfig(),
): Promise<SubscriptionActionResult> {
  const nativeBridge = await waitForNativeBridge();
  if (nativeBridge) {
    return restoreWithNativeBridge(nativeBridge);
  }

  if (!canUseMockPurchases(config)) {
    return { status: 'unavailable', subscription: load() };
  }

  const subscription = load();
  if (hasActivePro()) {
    return { status: 'restored', subscription };
  }
  return { status: 'not-found', subscription };
}

export function useSubscription(): {
  tier: Tier;
  isPro: boolean;
} {
  const [state, setState] = useState<State>(() => load());
  useEffect(() => {
    const listener = () => setState(load());
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  const active = isActiveSubscription(state);
  return { tier: active ? 'pro' : 'free', isPro: active };
}

async function purchaseUnlockWithNativeBridge(
  nativeBridge: NativePurchaseBridge,
): Promise<SubscriptionActionResult> {
  try {
    const receipt = await nativeBridge.purchaseUnlock();
    return actionResultFromNativeReceipt(receipt, 'purchased');
  } catch {
    return { status: 'unavailable', subscription: load() };
  }
}

async function restoreWithNativeBridge(
  nativeBridge: NativePurchaseBridge,
): Promise<SubscriptionActionResult> {
  try {
    const receipt = await nativeBridge.restorePurchases();
    return actionResultFromNativeReceipt(receipt, 'restored');
  } catch {
    return { status: 'unavailable', subscription: load() };
  }
}

function actionResultFromNativeReceipt(
  receipt: NativePurchaseReceipt,
  successStatus: Extract<SubscriptionActionStatus, 'purchased' | 'restored'>,
): SubscriptionActionResult {
  if (receipt?.active) {
    const subscription: State = {
      tier: 'pro',
      source: 'apple',
      updatedAt: Date.now(),
    };
    save(subscription);
    return { status: successStatus, subscription };
  }

  return { status: inactiveStatusFor(receipt?.outcome, successStatus), subscription: load() };
}

/**
 * Map a non-entitling native outcome onto a UI status.
 *
 * When the native build predates the `outcome` field, a failed *purchase* is overwhelmingly
 * a user cancel (both StoreKit and Play Billing resolve `active: false` for it), so it
 * degrades to the silent `cancelled` rather than to a "no purchase found" message that
 * would read as an error immediately after the customer tapped Cancel.
 */
function inactiveStatusFor(
  outcome: NativePurchaseOutcome | undefined,
  successStatus: Extract<SubscriptionActionStatus, 'purchased' | 'restored'>,
): SubscriptionActionStatus {
  switch (outcome) {
    case 'cancelled':
      return 'cancelled';
    case 'pending':
      return 'pending';
    case 'unavailable':
      return 'unavailable';
    case 'not-found':
      return 'not-found';
    default:
      return successStatus === 'purchased' ? 'cancelled' : 'not-found';
  }
}

/** Non-consumables never expire: active just means the tier is pro and the source is trusted. */
function isActiveSubscription(
  state: State,
  config: PurchaseRuntimeConfig = readRuntimeConfig(),
): boolean {
  if (state.tier !== 'pro') return false;
  if (state.source === 'apple') return true;
  return canUseMockPurchases(config);
}

/**
 * Detect whether we're running inside a Capacitor iOS shell. The native bridge
 * user-script runs at document-start but may finish after React's first render.
 * Knowing we're on native iOS lets us show the purchase UI immediately and wait
 * for the bridge at purchase time rather than hiding the whole paywall.
 */
function isNativeIosContext(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  if (cap?.isNativePlatform?.()) return true;
  if (cap?.getPlatform?.() === 'ios') return true;
  return false;
}

/**
 * Accept either spelling of the restore method.
 *
 * The iOS StoreKit bridge exposes `restorePurchases()`, but the Android Play Billing
 * plugin registers its method as `restore()`. The detection guard below requires
 * `restorePurchases`, so on Android the bridge was never recognised at all and purchases
 * silently fell through to the unavailable path — the same class of contract mismatch
 * that caused the iOS rejections, just on the other platform.
 */
function normalizeNativeBridge(bridge: unknown): NativePurchaseBridge | null {
  if (!bridge || typeof bridge !== 'object') return null;
  const candidate = bridge as Partial<NativePurchaseBridge> & {
    restore?: () => Promise<NativePurchaseReceipt>;
  };
  if (typeof candidate.purchaseUnlock !== 'function') return null;

  const restore =
    typeof candidate.restorePurchases === 'function'
      ? candidate.restorePurchases.bind(candidate)
      : typeof candidate.restore === 'function'
        ? candidate.restore.bind(candidate)
        : null;
  if (!restore) return null;

  return {
    purchaseUnlock: candidate.purchaseUnlock.bind(candidate),
    restorePurchases: restore,
    loadProduct:
      typeof candidate.loadProduct === 'function'
        ? candidate.loadProduct.bind(candidate)
        : undefined,
    checkEntitlement:
      typeof candidate.checkEntitlement === 'function'
        ? candidate.checkEntitlement.bind(candidate)
        : undefined,
  };
}

/**
 * Wait briefly for the native bridge to appear. On iOS, the WKUserScript that
 * defines `window.TruebendPurchases` runs at document-start, but there can be a
 * small window before it fully resolves — especially on slower devices or new
 * OS versions. Retry up to ~2 seconds before giving up.
 */
async function waitForNativeBridge(maxWaitMs = 2000): Promise<NativePurchaseBridge | null> {
  const bridge = getNativePurchaseBridge();
  if (bridge) return bridge;

  if (!isNativeIosContext()) return null;

  const interval = 100;
  const maxAttempts = Math.ceil(maxWaitMs / interval);
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((r) => setTimeout(r, interval));
    const found = getNativePurchaseBridge();
    if (found) return found;
  }

  return null;
}

function getNativePurchaseBridge(): NativePurchaseBridge | null {
  if (typeof window === 'undefined') return null;
  const bridge = normalizeNativeBridge(
    window.TruebendPurchases ?? window.Capacitor?.Plugins?.TruebendPurchases,
  );
  if (
    bridge &&
    typeof bridge.purchaseUnlock === 'function' &&
    typeof bridge.restorePurchases === 'function'
  ) {
    return bridge;
  }
  return null;
}

function readRuntimeConfig(): PurchaseRuntimeConfig {
  return {
    mode: import.meta.env.MODE,
    enableMockPurchases:
      import.meta.env.DEV || import.meta.env.VITE_ENABLE_MOCK_PURCHASES === 'true',
  };
}
